<script>
/**
 * 정산관리 상세 페이지.
 *
 * trade_unified_ledger 단건의 전체 정보를 표시한다.
 *
 * 구성 섹션:
 * 1. 기본 정보: 원장코드, 거래유형, 발생일시, 정산기준일
 * 2. 거래 정보: 현물구분, 중량, 시세(원/g), 거래금액, 임가공비
 * 3. 수수료 정산: 총수수료, 본사수수료, 이용지점수수료, 추천지점수수료
 * 4. 연결 코드: 결제코드, 상품코드, 체결코드, 매수/매도 호가코드 (거래유형에 따라 표시 분기)
 * 5. 인출 정보: 이용지점, 인출예약일, 인출완료 여부 (PRODUCT_BUY 시에만 표시)
 * 6. 회원 정보: 매수회원, 매도회원
 *
 * 권한:
 * - HQ 관리자: 전체 조회
 * - 지점 관리자: rec_branch_code 또는 use_branch_code = 내 지점코드인 건만 (서버에서 NOT_FOUND 처리)
 */
import { getSettlementDetail } from '@/api/settlement.js'

export default {
  name: 'SettlementDetailPage',

  data() {
    return {
      /** 원장코드 (URL 파라미터) */
      tradeUnifiedLedgerCode: this.$route.params.tradeUnifiedLedgerCode,

      /* ── 상세 데이터 ── */
      detail: null,

      /* ── UI 상태 ── */
      loading: false,
    }
  },

  mounted() {
    this.fetchDetail()
  },

  watch: {
    '$route.params.tradeUnifiedLedgerCode'(newCode) {
      this.tradeUnifiedLedgerCode = newCode
      this.fetchDetail()
    },
  },

  computed: {
    /** PRODUCT_BUY 거래인지 여부 (인출 정보 섹션 표시 분기) */
    isProductBuy() {
      return this.detail?.tradeKind === 'PRODUCT_BUY'
    },

    /** P2P_ASK 거래인지 여부 (연결 코드 섹션 표시 분기) */
    isP2pAsk() {
      return this.detail?.tradeKind === 'P2P_ASK'
    },
  },

  methods: {
    /**
     * 서버에서 통합원장 상세 정보를 조회한다.
     * NOT_FOUND 응답이면 목록으로 리다이렉트.
     */
    async fetchDetail() {
      if (!this.tradeUnifiedLedgerCode) {
        this.$router.push('/settlement')
        return
      }
      this.loading = true
      try {
        const res = await getSettlementDetail(this.tradeUnifiedLedgerCode)
        if (res.data?.code === 0) {
          this.detail = res.data.data
        } else {
          // 권한 없거나 존재하지 않는 건 → 목록으로
          this.$router.push('/settlement')
        }
      } catch (e) {
        console.error('[SettlementDetailPage] fetchDetail error:', e)
        this.$router.push('/settlement')
      } finally {
        this.loading = false
      }
    },

    /** 목록으로 돌아가기 */
    goBack() {
      this.$router.push('/settlement')
    },

    /** 금액을 천단위 콤마 포맷으로 변환. null/빈 값이면 '-' 반환 */
    formatAmount(val) {
      if (val == null || val === '') return '-'
      return Number(val).toLocaleString() + '원'
    },

    /** 날짜/시간 포맷 (yyyy-MM-dd HH:mm) */
    formatDateTime(val) {
      if (!val) return '-'
      return String(val).substring(0, 16).replace('T', ' ')
    },

    /** 날짜 포맷 (yyyy-MM-dd) */
    formatDate(val) {
      if (!val) return '-'
      return String(val).substring(0, 10)
    },

    /** 소수점 포함 중량 포맷 */
    formatWeight(val) {
      if (val == null || val === '') return '-'
      return Number(val).toFixed(2) + 'g'
    },

    /**
     * 거래유형 chip 색상 반환.
     * P2P_ASK=호가(amber), PRODUCT_BUY=상품구매(teal)
     */
    getTradeKindColor(kind) {
      if (kind === 'P2P_ASK')     return 'amber-darken-2'
      if (kind === 'PRODUCT_BUY') return 'teal'
      return 'grey'
    },

    /**
     * 현물구분 chip 색상 반환.
     * GOLD=금(amber), SILVER=은(grey)
     */
    getSpotTypeColor(spotType) {
      if (spotType === 'GOLD')   return 'amber'
      if (spotType === 'SILVER') return 'grey'
      return 'primary'
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 (브레드크럼 + 제목) -->
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="d-flex align-center ga-1 text-caption text-medium-emphasis mb-1">
          <span class="breadcrumb-link text-teal-darken-1" @click="goBack">정산관리</span>
          <v-icon size="12">mdi-chevron-right</v-icon>
          <span>상세</span>
        </div>
        <div class="text-h5 font-weight-bold">정산 상세</div>
        <div class="text-body-2 text-grey mt-1">통합 거래 원장 상세 정보 및 수수료 정산 내역</div>
      </div>
      <v-btn
        variant="outlined"
        prepend-icon="mdi-arrow-left"
        size="small"
        @click="goBack"
      >
        목록으로
      </v-btn>
    </div>

    <!-- 로딩 스켈레톤 -->
    <v-row v-if="loading">
      <v-col v-for="i in 4" :key="i" cols="12" md="6">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <template v-else-if="detail">
      <v-row>

        <!-- ① 기본 정보 카드 -->
        <v-col cols="12" md="6">
          <v-card rounded="xl" elevation="0" class="detail-card h-100">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-teal-soft">
                <v-icon color="teal" size="18">mdi-file-document-outline</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">기본 정보</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="dl-grid">
                <div class="dl-item">
                  <div class="dl-label">원장코드</div>
                  <div class="dl-value text-caption font-weight-medium text-grey-darken-1">
                    {{ detail.tradeUnifiedLedgerCode || '-' }}
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">거래유형</div>
                  <div class="dl-value">
                    <v-chip
                      :color="getTradeKindColor(detail.tradeKind)"
                      size="x-small"
                      label
                      variant="tonal"
                      class="font-weight-medium"
                    >
                      {{ detail.tradeKindLabel || detail.tradeKind || '-' }}
                    </v-chip>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">발생일시</div>
                  <div class="dl-value">{{ formatDateTime(detail.occurredAt) }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">정산기준일</div>
                  <div class="dl-value">{{ formatDate(detail.settlementDate) }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ② 거래 정보 카드 -->
        <v-col cols="12" md="6">
          <v-card rounded="xl" elevation="0" class="detail-card h-100">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-amber-soft">
                <v-icon color="amber-darken-2" size="18">mdi-scale-balance</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">거래 정보</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="dl-grid">
                <div class="dl-item">
                  <div class="dl-label">현물구분</div>
                  <div class="dl-value">
                    <v-chip
                      v-if="detail.spotType"
                      :color="getSpotTypeColor(detail.spotType)"
                      size="x-small"
                      label
                      variant="tonal"
                      class="font-weight-medium"
                    >
                      {{ detail.spotTypeLabel || detail.spotType }}
                    </v-chip>
                    <span v-else class="text-medium-emphasis">-</span>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">시세(원/g)</div>
                  <div class="dl-value font-weight-medium">{{ formatAmount(detail.priceKrwPerG) }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">거래 중량</div>
                  <div class="dl-value font-weight-bold">{{ formatWeight(detail.weightG) }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">거래금액</div>
                  <div class="dl-value font-weight-bold">{{ formatAmount(detail.tradeAmount) }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">임가공비</div>
                  <div class="dl-value">{{ formatAmount(detail.conventionCost) }}</div>
                </div>
                <div class="dl-item" v-if="isProductBuy">
                  <div class="dl-label">보유차감 중량</div>
                  <div class="dl-value">{{ formatWeight(detail.holdDeductWeightG) }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ③ 수수료 정산 카드 (전체 너비) -->
        <v-col cols="12">
          <v-card rounded="xl" elevation="0" class="detail-card">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-green-soft">
                <v-icon color="green-darken-2" size="18">mdi-cash-multiple</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">수수료 정산</span>
              <v-spacer />
              <span class="text-caption text-medium-emphasis">총 수수료 = 본사 + 이용지점 + 추천지점</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="fee-grid">
                <!-- 총수수료 (강조) -->
                <div class="fee-item fee-item--total">
                  <div class="fee-icon-wrap bg-green-soft">
                    <v-icon color="green-darken-2" size="16">mdi-sigma</v-icon>
                  </div>
                  <div class="fee-label">총 수수료</div>
                  <div class="fee-value text-green-darken-2 font-weight-bold">
                    {{ formatAmount(detail.totalFeeAmount) }}
                  </div>
                </div>
                <!-- 본사 수수료: HQ만 표시 (지점 관리자에게는 null) -->
                <div class="fee-item" v-if="detail.hqFeeAmount != null">
                  <div class="fee-icon-wrap bg-primary-soft">
                    <v-icon color="primary" size="16">mdi-office-building-outline</v-icon>
                  </div>
                  <div class="fee-label">본사 수수료</div>
                  <div class="fee-value font-weight-medium">
                    {{ formatAmount(detail.hqFeeAmount) }}
                  </div>
                </div>
                <!--
                  이용지점 수수료: 내 지점코드 = use_branch_code 인 경우에만 서버가 값을 내려줌.
                  null이면 내 지점 관련 수수료가 아니므로 항목 자체를 숨김.
                -->
                <div class="fee-item" v-if="detail.useBranchFeeAmount != null">
                  <div class="fee-icon-wrap bg-teal-soft">
                    <v-icon color="teal" size="16">mdi-store-outline</v-icon>
                  </div>
                  <div class="fee-label">이용지점 수수료</div>
                  <div class="fee-value font-weight-medium">
                    {{ formatAmount(detail.useBranchFeeAmount) }}
                  </div>
                  <div class="fee-branch text-caption text-medium-emphasis" v-if="detail.useBranchName">
                    {{ detail.useBranchName }}
                  </div>
                </div>
                <!--
                  추천지점 수수료: 내 지점코드 = rec_branch_code 인 경우에만 서버가 값을 내려줌.
                  null이면 내 지점 관련 수수료가 아니므로 항목 자체를 숨김.
                -->
                <div class="fee-item" v-if="detail.recBranchFeeAmount != null">
                  <div class="fee-icon-wrap bg-amber-soft">
                    <v-icon color="amber-darken-2" size="16">mdi-star-outline</v-icon>
                  </div>
                  <div class="fee-label">추천지점 수수료</div>
                  <div class="fee-value font-weight-medium">
                    {{ formatAmount(detail.recBranchFeeAmount) }}
                  </div>
                  <div class="fee-branch text-caption text-medium-emphasis" v-if="detail.recBranchName">
                    {{ detail.recBranchName }}
                  </div>
                </div>
                <!-- 표시 가능한 수수료 항목이 총수수료 외에 없을 때 안내 문구 -->
                <div
                  v-if="detail.hqFeeAmount == null && detail.useBranchFeeAmount == null && detail.recBranchFeeAmount == null"
                  class="text-caption text-medium-emphasis mt-1"
                  style="grid-column: span 4;"
                >
                  내 지점에 해당하는 수수료 정보가 없습니다.
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ④ 회원 정보 카드 -->
        <v-col cols="12" md="6">
          <v-card rounded="xl" elevation="0" class="detail-card h-100">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-primary-soft">
                <v-icon color="primary" size="18">mdi-account-group-outline</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">회원 정보</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="dl-grid">
                <div class="dl-item">
                  <div class="dl-label">매수회원</div>
                  <div class="dl-value">
                    <span class="font-weight-medium">{{ detail.buyerName || '-' }}</span>
                    <span v-if="detail.buyerUserCode" class="text-caption text-medium-emphasis ml-1">
                      ({{ detail.buyerUserCode }})
                    </span>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">매도회원</div>
                  <div class="dl-value">
                    <span class="font-weight-medium">{{ detail.sellerName || '-' }}</span>
                    <span v-if="detail.sellerUserCode" class="text-caption text-medium-emphasis ml-1">
                      ({{ detail.sellerUserCode }})
                    </span>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">이용지점</div>
                  <div class="dl-value">
                    <template v-if="detail.useBranchName">
                      <span class="font-weight-medium">{{ detail.useBranchName }}</span>
                      <span v-if="detail.useBranchCode" class="text-caption text-medium-emphasis ml-1">
                        ({{ detail.useBranchCode }})
                      </span>
                    </template>
                    <span v-else class="text-medium-emphasis">{{ detail.useBranchCode || '-' }}</span>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">추천지점</div>
                  <div class="dl-value">
                    <template v-if="detail.recBranchName">
                      <span class="font-weight-medium">{{ detail.recBranchName }}</span>
                      <span v-if="detail.recBranchCode" class="text-caption text-medium-emphasis ml-1">
                        ({{ detail.recBranchCode }})
                      </span>
                    </template>
                    <span v-else class="text-medium-emphasis">{{ detail.recBranchCode || '-' }}</span>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ⑤ 연결 코드 카드 -->
        <v-col cols="12" md="6">
          <v-card rounded="xl" elevation="0" class="detail-card h-100">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-purple-soft">
                <v-icon color="purple" size="18">mdi-link-variant</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">연결 코드</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="dl-grid">
                <!-- 결제코드 (PRODUCT_BUY 관련) -->
                <div class="dl-item" v-if="detail.paymentCode">
                  <div class="dl-label">결제코드</div>
                  <div class="dl-value text-caption font-weight-medium text-grey-darken-1">
                    {{ detail.paymentCode }}
                  </div>
                </div>
                <!-- 상품코드 (PRODUCT_BUY 관련) -->
                <div class="dl-item" v-if="detail.productCode">
                  <div class="dl-label">상품코드</div>
                  <div class="dl-value text-caption font-weight-medium text-grey-darken-1">
                    {{ detail.productCode }}
                  </div>
                </div>
                <!-- 체결코드 (P2P 관련) -->
                <div class="dl-item" v-if="detail.p2pAskTradeCode">
                  <div class="dl-label">체결코드</div>
                  <div class="dl-value text-caption font-weight-medium text-grey-darken-1">
                    {{ detail.p2pAskTradeCode }}
                  </div>
                </div>
                <!-- 매수호가코드 -->
                <div class="dl-item" v-if="detail.p2pAskBuyCode">
                  <div class="dl-label">매수 호가코드</div>
                  <div class="dl-value text-caption font-weight-medium text-grey-darken-1">
                    {{ detail.p2pAskBuyCode }}
                  </div>
                </div>
                <!-- 매도호가코드 -->
                <div class="dl-item" v-if="detail.p2pAskSellCode">
                  <div class="dl-label">매도 호가코드</div>
                  <div class="dl-value text-caption font-weight-medium text-grey-darken-1">
                    {{ detail.p2pAskSellCode }}
                  </div>
                </div>
                <!-- 호가 수수료 정산코드 -->
                <div class="dl-item" v-if="detail.p2pAskFeeSettlementCode">
                  <div class="dl-label">정산코드</div>
                  <div class="dl-value text-caption font-weight-medium text-grey-darken-1">
                    {{ detail.p2pAskFeeSettlementCode }}
                  </div>
                </div>
                <!-- 적용 정책코드 -->
                <div class="dl-item" v-if="detail.tradePolicyCode">
                  <div class="dl-label">적용 정책</div>
                  <div class="dl-value text-caption font-weight-medium text-grey-darken-1">
                    {{ detail.tradePolicyCode }}
                  </div>
                </div>
                <!-- 연결 코드 없음 -->
                <div
                  v-if="!detail.paymentCode && !detail.productCode && !detail.p2pAskTradeCode
                         && !detail.p2pAskBuyCode && !detail.p2pAskSellCode"
                  class="text-caption text-medium-emphasis"
                >
                  연결된 코드 정보가 없습니다.
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ⑥ 인출 정보 카드 (PRODUCT_BUY 시에만 표시) -->
        <v-col cols="12" v-if="isProductBuy">
          <v-card rounded="xl" elevation="0" class="detail-card">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-orange-soft">
                <v-icon color="orange-darken-2" size="18">mdi-package-variant-closed</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">인출 정보</span>
              <v-spacer />
              <v-chip
                v-if="detail.withdrawnYn === 'Y'"
                size="x-small"
                color="success"
                variant="tonal"
                label
              >
                인출완료
              </v-chip>
              <v-chip
                v-else
                size="x-small"
                color="warning"
                variant="tonal"
                label
              >
                미인출
              </v-chip>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="dl-grid dl-grid--4">
                <div class="dl-item">
                  <div class="dl-label">이용지점</div>
                  <div class="dl-value">
                    <template v-if="detail.useBranchName">
                      <span class="font-weight-medium">{{ detail.useBranchName }}</span>
                      <span v-if="detail.useBranchCode" class="text-caption text-medium-emphasis ml-1">
                        ({{ detail.useBranchCode }})
                      </span>
                    </template>
                    <span v-else class="text-medium-emphasis">{{ detail.useBranchCode || '-' }}</span>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">인출예약일</div>
                  <div class="dl-value">{{ formatDate(detail.withdrawalReserveDate) }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">인출완료여부</div>
                  <div class="dl-value">
                    <v-chip
                      size="x-small"
                      :color="detail.withdrawnYn === 'Y' ? 'success' : 'grey'"
                      variant="tonal"
                      label
                    >
                      {{ detail.withdrawnYn === 'Y' ? '완료' : '미완료' }}
                    </v-chip>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">인출완료일시</div>
                  <div class="dl-value">{{ formatDateTime(detail.withdrawnDt) }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

      </v-row>
    </template>
  </div>
</template>

<style scoped>
/* ── 공통 카드 ── */
.detail-card {
  border: 1px solid rgba(107, 114, 128, 0.32) !important;
}

.detail-card-header {
  background: rgba(37, 43, 57, 0.72);
}

/* ── 헤더 아이콘 ── */
.header-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
}

.bg-teal-soft    { background: rgba(0, 150, 136, 0.1); }
.bg-amber-soft   { background: rgba(255, 193, 7, 0.12); }
.bg-green-soft   { background: rgba(76, 175, 80, 0.1); }
.bg-primary-soft { background: rgba(var(--v-theme-primary), 0.1); }
.bg-purple-soft  { background: rgba(156, 39, 176, 0.1); }
.bg-orange-soft  { background: rgba(255, 152, 0, 0.1); }

/* ── 브레드크럼 ── */
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

/* ── 정의 리스트 (2열) ── */
.dl-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px 16px;
}

/* 4열 레이아웃 (인출 정보 등) */
.dl-grid--4 {
  grid-template-columns: repeat(4, 1fr);
}

@media (max-width: 960px) {
  .dl-grid--4 {
    grid-template-columns: repeat(2, 1fr);
  }
}

.dl-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dl-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #9fb0c9;
  text-transform: uppercase;
}

.dl-value {
  font-size: 0.875rem;
  color: #e5e7eb;
  line-height: 1.4;
}

/* ── 수수료 카드 그리드 ── */
.fee-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

@media (max-width: 960px) {
  .fee-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.fee-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 16px;
  border-radius: 12px;
  background: rgba(37, 43, 57, 0.5);
}

/* 총수수료 항목 강조 */
.fee-item--total {
  background: rgba(76, 175, 80, 0.07);
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.fee-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  flex-shrink: 0;
}

.fee-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #9fb0c9;
  text-transform: uppercase;
}

.fee-value {
  font-size: 0.95rem;
  color: #e5e7eb;
}

.fee-branch {
  line-height: 1.2;
}
</style>
